// SPDX-License-Identifier: Elastic-2.0
/**
 * WizardShell — pure chrome for the onboarding wizard (T-013).
 *
 * Renders the persistent frame around whatever step body is currently
 * active: full-bleed GradientOrb behind, header (Mirlai mark + wordmark
 * | Stepper | ThemePill + counter), centered content slot, footer with
 * Back / (optional secondary) / Next.
 *
 * This component has NO step-routing or per-step state — `OnboardingPage`
 * owns navigation and feeds `step`, `canNext`, `nextLabel`, callbacks,
 * etc. Keeping chrome pure means future redesigns (full-screen modals,
 * landscape tablet layouts, etc.) can swap the chrome with no logic
 * churn.
 *
 * Visual contract: `WizardShell.jsx` in the design-system reference
 * (`mirlai-os/design-system/ui_kits/onboarding/WizardShell.jsx`).
 */
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button";
import { GradientOrb } from "@/components/GradientOrb/GradientOrb";
import { MirlaiMark } from "@/components/MirlaiMark/MirlaiMark";
import { Stepper } from "@/components/Stepper/Stepper";
import { ThemePill } from "@/components/ThemePill/ThemePill";

import styles from "./WizardShell.module.css";

export interface WizardShellProps {
  /** Current step index (0-based). Drives Stepper + counter. */
  step: number;
  /** Total step count — usually `TOTAL_STEPS` from `onboardingSteps`. */
  totalSteps: number;
  /** Body content for the active step. */
  children: ReactNode;
  /**
   * `key` used by the host to remount `children` on step change so the
   * CSS-only fade-in animation runs. Optional — when omitted the chrome
   * still renders correctly, just with no entrance animation.
   */
  stepKey?: string | number;
  /**
   * Back-button handler. The shell disables the button when `step === 0`
   * so the host doesn't have to special-case the boundary.
   */
  onBack: () => void;
  /** Next-button handler. Disabled when `canNext` is false. */
  onNext: () => void;
  /** Whether the Next button is enabled. */
  canNext: boolean;
  /**
   * Label shown on the primary next-button — typically
   * `t("onboarding.continue")` or `t("onboarding.start")` on A5→A6.
   */
  nextLabel: string;
  /**
   * Optional secondary control rendered between the gap and the
   * primary next-button. Used by A5 to surface the "Skip — set up later"
   * (account-less) action.
   */
  secondary?: ReactNode;
  /**
   * Hides the entire footer row. Used by A6 ("magic moment") where
   * auto-advance owns the experience and there's no user input.
   */
  hideFooter?: boolean;
}

/**
 * Pure-chrome wrapper. Mounted by `OnboardingPage`.
 *
 * Layering:
 *  - `.shell` is the full-bleed root (theme-aware background + color)
 *  - `<GradientOrb />` sits at z-index 0 (decorative)
 *  - `.layout` is z-index 1 — header / content / footer all here
 */
export function WizardShell({
  step,
  totalSteps,
  children,
  stepKey,
  onBack,
  onNext,
  canNext,
  nextLabel,
  secondary,
  hideFooter = false,
}: WizardShellProps): JSX.Element {
  const { t } = useTranslation();

  return (
    <div className={styles.shell} data-testid="wizard-shell">
      <GradientOrb variant="full" />

      <div className={styles.layout}>
        {/* Header — brand · Stepper · controls */}
        <header className={styles.header}>
          <div className={styles.brand}>
            <MirlaiMark size={28} />
            <span className={styles.wordmark}>mirlai</span>
          </div>

          <div className={styles.stepperWrap}>
            <Stepper steps={totalSteps} current={step} />
          </div>

          <div className={styles.controls}>
            <ThemePill />
            <div className={styles.counter} data-testid="wizard-counter">
              {t("onboarding.step_of", { current: step + 1, total: totalSteps })}
            </div>
          </div>
        </header>

        {/* Content — step body in a single fade-in layer */}
        <main className={styles.content}>
          <div className={styles.contentInner}>
            <div className={styles.stepStage}>
              <div
                key={stepKey ?? step}
                className={`${styles.stepLayer} ${styles.stepLayerEntering}`}
                data-testid="wizard-step-layer"
              >
                {children}
              </div>
            </div>
          </div>
        </main>

        {/* Footer — Back | secondary + Next */}
        {!hideFooter && (
          <footer className={styles.footer} data-testid="wizard-footer">
            <div className={styles.footerLeft}>
              <Button
                variant="ghost"
                onClick={onBack}
                disabled={step === 0}
                data-testid="wizard-back"
              >
                {`← ${t("onboarding.back")}`}
              </Button>
            </div>
            <div className={styles.footerRight}>
              {secondary}
              <Button
                variant="primary"
                onClick={onNext}
                disabled={!canNext}
                data-testid="wizard-next"
              >
                {nextLabel}
              </Button>
            </div>
          </footer>
        )}
      </div>
    </div>
  );
}
